<template>
  <v-card variant="outlined" rounded="lg" class="raci-table">
    <v-toolbar density="compact">
      <v-toolbar-title>2.1.1 RACI Matrix</v-toolbar-title>
    </v-toolbar>
    <v-table fixed-header density="compact">
      <thead>
        <tr>
          <th class="normal-header task">
            <div><span>Tasks</span></div>
          </th>
          <th
            v-for="role in plan?.raci_roles ?? []"
            :key="role.id"
            class="rotated-header"
          >
            <div class="container">
              <span class="entry">
                <RoleAutocomplete
                  v-model="role.role_id"
                  class="role"
                  :readonly="readonly"
                />
              </span>
            </div>
          </th>
          <th class="normal-header">
            <v-btn
              v-if="!readonly"
              class="ml-4"
              icon="mdi-plus"
              variant="plain"
              density="compact"
              color="secondary"
              @click="emit('add:role')"
            />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="task in plan?.raci_tasks ?? []" :key="task.id">
          <th class="task">
            <v-text-field
              v-model="task.name"
              density="compact"
              hide-details
              variant="plain"
              placeholder="Task"
              :readonly="readonly"
            />
          </th>
          <td
            v-for="role in plan?.raci_roles ?? []"
            :key="role.id"
            class="pa-0"
          >
            <v-menu>
              <template v-slot:activator="{ props }">
                <v-btn
                  variant="text"
                  class="raci-value"
                  :color="getRACI(task, role).color"
                  v-bind="props"
                  :readonly="readonly"
                >
                  {{ getRACI(task, role).text }}
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  title=""
                  @click="emit('set:raci', { task, role, value: null })"
                />
                <v-list-item
                  title="Responsible"
                  base-color="blue"
                  @click="emit('set:raci', { task, role, value: 'R' })"
                />
                <v-list-item
                  title="Accountable"
                  base-color="red"
                  @click="emit('set:raci', { task, role, value: 'A' })"
                />
                <v-list-item
                  title="Consulted"
                  base-color="green"
                  @click="emit('set:raci', { task, role, value: 'C' })"
                />
                <v-list-item
                  title="Informed"
                  base-color="black"
                  @click="emit('set:raci', { task, role, value: 'I' })"
                />
              </v-list>
            </v-menu>
          </td>
          <td>
            <v-btn
              v-if="!readonly"
              icon="mdi-delete"
              variant="plain"
              density="compact"
              color="red"
              @click="emit('delete:task', task)"
            />
          </td>
        </tr>
        <tr v-if="!readonly">
          <th>
            <v-btn
              icon="mdi-plus"
              variant="plain"
              density="compact"
              color="secondary"
              @click="emit('add:task')"
            />
          </th>

          <td v-for="role in plan?.raci_roles ?? []" :key="role.id">
            <v-btn
              icon="mdi-delete"
              variant="plain"
              density="compact"
              color="red"
              @click="emit('delete:role', role)"
            />
          </td>
          <td></td>
        </tr>
      </tbody>
    </v-table>
  </v-card>
</template>

<script setup lang="ts">
import { AssetManagementPlan, RACIRole, RACITask } from "@/classes";
import { RACIValue } from "@/classes/asset-management-plan";
import RoleAutocomplete from "../RoleAutocomplete.vue";

const props = defineProps<{
  plan: AssetManagementPlan | null;
  readonly?: boolean;
}>();

const emit = defineEmits<{
  "add:task": [];
  "add:role": [];
  "delete:task": [RACITask];
  "delete:role": [RACIRole];
  "set:raci": [{ task: RACITask; role: RACIRole; value: RACIValue | null }];
}>();

const getRACI = (task: RACITask, role: RACIRole) => {
  const data = props.plan?.raci_data.find(
    (d) => d.raci_role_id === role.id && d.raci_task_id === task.id,
  );

  if (data) {
    switch (data.value) {
      case "R":
        return { color: "blue", text: "R" };
      case "A":
        return { color: "red", text: "A" };
      case "C":
        return { color: "green", text: "C" };
      case "I":
        return { color: "black", text: "I" };
      default:
        return { color: "", text: "" };
    }
  } else {
    return { color: "", text: "" };
  }
};
</script>

<style lang="scss" scoped>
.raci-table {
  width: 100%;

  .raci-value {
    width: 100%;
    height: 100%;
    margin: 0px;
    border-radius: 0px;
  }

  .task {
    width: 350px;
    min-width: 350px;
    max-width: 350px;
  }
  .role {
    width: 150px;
    min-width: 150px;
    max-width: 150px;
  }

  .normal-header {
    background: transparent !important;
    align-content: end;
  }
  td {
    border-collapse: collapse !important;
    border-spacing: 0px;
    border-right: thin solid
      rgba(var(--v-border-color), var(--v-border-opacity)) !important;
    border-left: thin solid rgba(var(--v-border-color), var(--v-border-opacity)) !important;
  }

  //https://www.jimmybonney.com/articles/column_header_rotation_css/
  .rotated-header {
    background: transparent !important;
    height: 160px;
    width: 80px;
    min-width: 80px;
    max-width: 80px;
    position: relative;
    vertical-align: bottom;
    padding: 0;
    font-size: 12px;
    line-height: 0.8;
    pointer-events: none;
    .container {
      position: relative;
      top: 0px;
      left: 80px; /* 80 * tan(45) / 2 = 40 where 80 is the height on the cell and 45 is the transform angle*/
      height: 100%;
      -ms-transform: skew(-45deg, 0deg);
      -moz-transform: skew(-45deg, 0deg);
      -webkit-transform: skew(-45deg, 0deg);
      -o-transform: skew(-45deg, 0deg);
      transform: skew(-45deg, 0deg);
      overflow: hidden;
      border-left: 1px solid #dddddd;
      border-right: 1px solid #dddddd;
      pointer-events: none;
      .entry {
        -ms-transform: skew(45deg, 0deg) rotateZ(315deg);
        -moz-transform: skew(45deg, 0deg) rotateZ(315deg);
        -webkit-transform: skew(45deg, 0deg) rotateZ(315deg);
        -o-transform: skew(45deg, 0deg) rotateZ(315deg);
        transform: skew(45deg, 0deg) rotateZ(315deg);
        position: absolute;
        bottom: 40px; /* 40 cos(45) = 28 with an additional 2px margin*/
        left: -40px; /*Because it looked good, but there is probably a mathematical link here as well*/
        display: inline-block;
        width: 170px; /* 80 / cos(45) - 40 cos (45) = 85 where 80 is the height of the cell, 40 the width of the cell and 45 the transform angle*/
        text-align: left;
        overflow: hidden;
        pointer-events: all;
      }
    }
  }
}
</style>
